import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { CycleDetailView } from '@/routes/features/projects/components/cycles/CycleDetailView'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/cycles/$cycleId',
)({
  component: SpaceCycleDetailsPage,
})

export default function SpaceCycleDetailsPage() {
  const { t } = useTranslation('projects')
  const { workspaceSlug, spaceKey, projectKey, cycleId } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()

  const space = useMemo<ProjectDocument | null>(() => {
    return rootProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)

  const project = useMemo<ProjectDocument | null>(() => {
    return childProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
      return true
    }) ?? null
  }, [childProjects, projectKey])

  const breadcrumbContent = useMemo(() => {
    if (!space || !project) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={space.iconType} icon={space.icon} iconColor={space.iconColor} size={14} />
          <span className="truncate">{space.name}</span>
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          search={{ tab: 'projects' }}
          className="hover:text-foreground transition-colors truncate"
        >
          {t('tabs.projects')}
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey"
          params={{ workspaceSlug, spaceKey, projectKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />
          <span className="truncate">{project.name || 'Untitled project'}</span>
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey"
          params={{ workspaceSlug, spaceKey, projectKey }}
          search={{ projectTab: 'cycles' }}
          className="hover:text-foreground transition-colors truncate"
        >
          {t('tabs.cycles')}
        </Link>
      </>
    )
  }, [space, project, workspaceSlug, spaceKey, projectKey, t])

  usePageLayout({
    sidebarContent: <TaskSidebar />,
    breadcrumb: breadcrumbContent,
  }, [space?.id, space?.name, space?.icon, space?.iconType, space?.iconColor, project?.id, project?.name, project?.icon, project?.iconType, project?.iconColor, workspaceSlug, spaceKey, projectKey])

  if (spacesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !project) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Project not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <CycleDetailView
      cycleId={cycleId}
      projectId={project.id}
      onBack={() => {
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
          params: { workspaceSlug, spaceKey, projectKey },
        })
      }}
      onComplete={() => {
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
          params: { workspaceSlug, spaceKey, projectKey },
          search: { projectTab: 'cycles' },
        })
      }}
    />
  )
}
